import Container from '@/components/container';
import { Divider, Form, Row, Col, message, Pagination, Spin, Radio } from 'antd';
import React, { useEffect, useState, useMemo, useContext } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import Checktag from '@/components/checktag';
import './list-content.less';
import apis from '@/apis/generic/mac/square';
import CardItem from './card-item.js';
import LOAD_STATUS from '@/constants/load-status';
import ComponentLog from '@/components/component-log';
import { filterConditions } from '@/common/utils';
import ContextProviderMenus from '@/components/slide-menu/provide-slide-menu';
import { useConfig } from '@/apis/ducc';
import { exposureLog, typeLog } from '@/pages/all-about-me/components/signed-activity/log.js';
import Empty from '@/components/core-empty';
import FilterSearchForm from './filterSearchForm';
import PopGuide from './pop-guide';
import { isInC2B } from '@/common/utils/beInectedIn';

const initConditions = {
  page: 1,
  pageSize: 15,
  marketSceneTags: [],
  activityTypeTags: [],
  activityFeatureTags: [],
  cardActivityName: '',
  cardActivityId: '',
  minActivityBeginTime: '',
  maxActivityBeginTime: '',
  orderBy: 'RELATE_TO_ME', // 默认按照最新发布时间倒序排序
};

function ListContent(props) {
  // 百亿补贴打标加上ducc配置
  const [getConfig, config] = useConfig();
  const signedActivityAtmosphereLabel = getConfig('signedActivityAtmosphereLabel', '[]');
  //-------------
  const { queryType, hideQuery } = props;
  const [form] = Form.useForm();
  const [data, setData] = useState({
    page: 1,
    pageSize: 15,
    totalCount: 0,
    totalPage: 0,
    items: [],
  });
  const [pageLoading, setPageLoading] = useState(LOAD_STATUS.INIT);
  const [selectMode, setSelectMode] = useState('single'); //single:单选， multiple：多选
  const [conditions, setConditions] = useState(initConditions);

  useEffect(() => {
    // 货主相关角色定位并选中某些参数
    let CargoParams = {};
    try {
      const params = window.sessionStorage.getItem('cargoOwnerParams');
      if (params) {
        CargoParams = JSON.parse(params);
        window.sessionStorage.setItem('cargoOwnerParams', '');
      }
    } catch (e) {
      console.error(e);
    }

    setConditions(
      Object.assign({}, initConditions, { queryType }, isInC2B() ? { cardActivityType: 33 } : {}, CargoParams)
    );
  }, [queryType]);

  const commitSearch = (query) => {
    setConditions(Object.assign({}, conditions, query, { page: 1 }));
  };

  const label = useContext(ContextProviderMenus).label;
  const labelMap = useMemo(() => {
    if (!label) return {};
    let self = {};
    label.marketSceneTags?.map((ele) => (self[ele.code] = ele));
    label.activityTypeTags?.map((ele) => (self[ele.code] = ele));
    label.activityFeatureTags?.map((ele) => (self[ele.code] = ele));
    return self;
  }, [label]);

  const tagsMap = {
    marketScene: {
      label: '营销场景',
      selected: conditions.marketSceneTags,
      log: 'marketingscene_select',
      change(value) {
        if (selectMode == 'single') {
          commitSearch({ marketSceneTags: value[0] ? [value[0]] : [], activityTypeTags: [], activityFeatureTags: [] });
        } else {
          if (value.length <= 5) commitSearch({ marketSceneTags: value });
          else message.warning('营销场景一次最多可选择5个哦～');
        }
      },
    },
    activityType: {
      label: '活动玩法',
      selected: conditions.activityTypeTags,
      log: 'activitytype_select',
      change(value) {
        if (selectMode == 'single') {
          commitSearch({ activityTypeTags: value[0] ? [value[0]] : [], marketSceneTags: [], activityFeatureTags: [] });
        } else {
          if (value.length <= 5) commitSearch({ activityTypeTags: value });
          else message.warning('活动类型一次最多可选择5个哦～');
        }
      },
    },
    activityFeature: {
      label: '活动特色',
      selected: conditions.activityFeatureTags,
      log: null,
      change(value) {
        if (selectMode == 'single') {
          commitSearch({ activityFeatureTags: value[0] ? [value[0]] : [], marketSceneTags: [], activityTypeTags: [] });
        } else {
          if (value.length <= 5) commitSearch({ activityFeatureTags: value });
          else message.warning('活动特色一次最多可选择5个哦～');
        }
      },
    },
  };

  const onSelectedChange = (tags) => {
    let marketSceneTags = [];
    let activityTypeTags = [];
    let activityFeatureTags = [];
    tags.map((item) => {
      labelMap[item].classify == 2 && activityTypeTags.push(item);
      labelMap[item].classify == 1 && marketSceneTags.push(item);
      labelMap[item].classify == 4 && activityFeatureTags.push(item);
    });
    commitSearch({ marketSceneTags, activityTypeTags, activityFeatureTags });
  };

  // 列表数据
  useEffect(() => {
    if (!conditions.queryType) return;
    let mount = true;
    setPageLoading(LOAD_STATUS.ING);
    apis
      .page(filterConditions({ ...conditions, orderBy: conditions.orderBy || 'CARD_PUBLISH_TIME_DESC' }))
      .then((res) => {
        if (!mount) return;
        const { ret, data, msg } = res;
        if (ret != '0' || !data) throw msg || '广场数据请求失败';
        setPageLoading(LOAD_STATUS.FIRST);
        setData(data);
        // 获取二次请求的参数
        const squareActivityExtQueries = [];
        const logParams = [];
        data.items.map((item) => {
          const { cardActivityId, cardActivityType, cardId } = item;
          logParams.push(cardId);
          squareActivityExtQueries.push({ cardActivityId, cardActivityType });
        });
        exposureLog(logParams.join(','));
        return squareActivityExtQueries;
      })
      .then((squareActivityExtQueries) => {
        if (!mount) return;
        if (!squareActivityExtQueries.length) return;
        apis
          .batchQueryActivityExtInfo({ squareActivityExtQueryStr: JSON.stringify(squareActivityExtQueries) })
          .then((_res) => {
            if (!mount) return;
            const { ret, data, msg } = _res;
            if (ret != '0' || !data.data) throw msg || '广场数据请求二次接口失败';
            setPageLoading(LOAD_STATUS.COMPLETED);
            const secondDataMap = {};
            data.data.map((ele) => {
              secondDataMap[ele.cardId] = ele;
            });

            setData((firstData) => {
              const joinData = firstData.items.map((item) => {
                return { ...item, ...secondDataMap[item.cardId] };
              });
              return Object.assign({}, firstData, { items: joinData });
            });
          });
      })
      .catch((ex) => {
        setPageLoading(LOAD_STATUS.FAILED);
        message.error(ex);
      });
    return () => {
      mount = false;
    };
  }, [conditions]);

  const getAtmosphereLabel = (code) => {
    const label = JSON.parse(signedActivityAtmosphereLabel).find((ele) => ele.code === code);
    return label && <img className="signed-activities-filter-tags-atmosphere-label" src={label.img} />;
  };
  const renderCheckTag = (type, list) => {
    const onChange = tagsMap[type].change;
    const selectTags = tagsMap[type].selected;
    return list?.length ? (
      <div className="signed-activities-filter-tags">
        <div className="tags-label">
          {tagsMap[type].label +
            (selectTags.length && selectMode == 'multiple' ? `(${selectTags.length}/5)` : '') +
            '：'}
        </div>
        <Checktag.ChecktagGroup value={selectTags} onChange={onChange}>
          {list?.map((item) => (
            <Checktag className="signed-activities-filter-tag" key={item.code} value={item.code}>
              {/**给百亿补贴的tag加标签*/}
              {getAtmosphereLabel(item.code)}
              <span
                onClick={() => {
                  window.log('yx_center', tagsMap[type].log, item.name);
                  typeLog(item.code);
                }}
              >
                {item.name}
              </span>
            </Checktag>
          ))}
        </Checktag.ChecktagGroup>
      </div>
    ) : null;
  };

  return (
    <div className="all-about-me-signed-activity-list">
      {!hideQuery && (
        <Container
          marginSize="max"
          margin={{ left: false, right: false, horizontal: true }}
          padding={{ vertical: true }}
          style={{ paddingBottom: '24px' }}
        >
          {!label.marketSceneTags.length && !label.activityTypeTags.length ? null : (
            <div className="all-about-me-signed-activity-tags">
              <div className="signed-activities-filter-tags-selector">
                {renderCheckTag('marketScene', label.marketSceneTags)}
                {renderCheckTag('activityType', label.activityTypeTags)}
                {renderCheckTag('activityFeature', label.activityFeatureTags)}
                {selectMode == 'single' ||
                (!conditions.activityTypeTags.length &&
                  !conditions.marketSceneTags.length &&
                  !conditions.activityFeatureTags.length) ? null : (
                  <div className="signed-activities-filter-tags-selected">
                    <Checktag.ChecktagGroup
                      value={conditions.marketSceneTags.concat(
                        conditions.activityTypeTags,
                        conditions.activityFeatureTags
                      )}
                      onChange={onSelectedChange}
                    >
                      {conditions.marketSceneTags.map((item) => (
                        <Checktag checktagtype="tag" checktagcloseable key={item} value={item}>
                          {labelMap[item].name}
                        </Checktag>
                      ))}
                      {conditions.activityTypeTags.map((item) => (
                        <Checktag checktagtype="tag" checktagcloseable key={item} value={item}>
                          {labelMap[item].name}
                        </Checktag>
                      ))}
                      {conditions.activityFeatureTags.map((item) => (
                        <Checktag checktagtype="tag" checktagcloseable key={item} value={item}>
                          {labelMap[item].name}
                        </Checktag>
                      ))}
                      <div
                        className="delete-tag-button"
                        onClick={() => {
                          onSelectedChange([]);
                        }}
                      >
                        清空
                        <DeleteOutlined className="delete-icon" />
                      </div>
                    </Checktag.ChecktagGroup>
                  </div>
                )}
              </div>
              <Radio.Group
                size="small"
                className="mode-select"
                value={selectMode}
                onChange={(e) => {
                  setSelectMode(e.target.value);
                  if (conditions.activityTypeTags.length || conditions.marketSceneTags.length) {
                    commitSearch({ activityTypeTags: [], marketSceneTags: [] });
                  }
                }}
              >
                <Radio.Button value="single">单选</Radio.Button>
                <Radio.Button value="multiple">多选</Radio.Button>
              </Radio.Group>
              <PopGuide />
            </div>
          )}
          <Divider className="acoro-divider" />
          <FilterSearchForm commitSearch={commitSearch} />
          <Radio.Group
            className="activity-card-sort"
            value={conditions.orderBy}
            onChange={(e) => commitSearch({ orderBy: e.target.value })}
          >
            <Radio key="RELATE_TO_ME" value="RELATE_TO_ME">
              与我相关
            </Radio>
            <ComponentLog>
              <span clstag="yx_center_1629184833805|85">
                <Radio key="CARD_PUBLISH_TIME_DESC" value="CARD_PUBLISH_TIME_DESC">
                  按最新发布时间
                </Radio>
              </span>
            </ComponentLog>
            <ComponentLog>
              <span clstag="yx_center_1629184833805|86">
                <Radio key="CARD_APPLY_END_TIME_ASC" value="CARD_APPLY_END_TIME_ASC">
                  按最近报名截止时间
                </Radio>
              </span>
            </ComponentLog>
          </Radio.Group>
        </Container>
      )}
      <Spin spinning={pageLoading == LOAD_STATUS.ING}>
        <Container margin={false} paddingSize="max" padding={{ left: false, top: false, bottom: true, right: false }}>
          {data.items?.length ? (
            <Row className="all-about-me-singed-act-card-page">
              {data.items.map((ele) => (
                <Col key={ele.id} span={8} className="all-about-me-page-card-item">
                  <CardItem
                    data={ele}
                    loading={pageLoading != LOAD_STATUS.FAILED && pageLoading != LOAD_STATUS.COMPLETED}
                  />
                </Col>
              ))}
            </Row>
          ) : (
            <Empty className="all-about-me-singed-act-card-page-empty" />
          )}
          <Pagination
            className="all-about-me-singed-act-card-page-pagination"
            current={conditions.page}
            total={data.totalCount}
            hideOnSinglePage
            itemRender={(page, type, originalElement) =>
              React.cloneElement(originalElement, { clstag: 'pageclick|keycount|yx_center_1629184833805|100' })
            }
            pageSize={conditions.pageSize}
            showSizeChanger={false}
            onChange={(page) => setConditions({ ...conditions, page })}
          />
        </Container>
      </Spin>
    </div>
  );
}

export default ListContent;
